import { segmentAnonymousId } from "@dreambigger/shared/src/hooks/segment";

export const BROWSER_ID_HEADER = "X-Cotribute-Browser-Id";
const STORAGE_KEY = "ct_browser_id";

const randomId = (): string => {
  const c = typeof window !== "undefined" ? window.crypto : undefined;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  // RFC 4122 v4 shape from Math.random — only reached on very old browsers.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    return (ch === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
};

// localStorage can throw (sandboxed iframe, storage disabled); then the id
// lives for this page session only, which still beats recording nothing.
const storage = {
  get: (): string | undefined => {
    try {
      return window.localStorage.getItem(STORAGE_KEY) || undefined;
    } catch {
      return undefined;
    }
  },
  set: (id: string): void => {
    try {
      window.localStorage.setItem(STORAGE_KEY, id);
    } catch {
      // see above
    }
  },
};

let pending: Promise<string> | undefined;

/**
 * One id per browser. The first visit takes Segment's anonymous id when it
 * loads in time (so the hash joins the backfilled Segment history), otherwise
 * a random one; it is then saved and every later visit reuses it, so a browser
 * does not hash differently depending on how fast analytics.js loaded.
 *
 * Only the two requests that store the id (as a hash) send it, each setting
 * `BROWSER_ID_HEADER` itself: the pageview row and the application create.
 * Resolution starts with the first of those, not at page load, so pages that
 * never record a view do not load Segment for it.
 */
export const browserId = (): Promise<string> => {
  pending ??= (async () => {
    const id = storage.get() ?? (await segmentAnonymousId()) ?? randomId();
    storage.set(id);
    return id;
  })();
  return pending;
};
